import { FlashList } from "@shopify/flash-list";
import { StyleSheet, View } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import Loading from "src/components/Loading";
import Touchable from "src/components/Touchable";
import abbreviateBigNumber from "src/helpers/abbreviateBigNumber";
import pluralS from "src/helpers/pluralS";
import useGatheringReactionAsset from "src/hooks/useGatheringReactionAsset";
import useStyle from "src/hooks/useStyle";
import IconClose from "src/icons/IconClose";
import { useInsideModal } from "src/modals";
import GatheringModal from "src/modals/configs/GatheringModal";
import { color } from "src/styles";
import {
	paddingLarge,
	paddingMedium,
	paddingSmallSpacer,
	paddingXL,
	paddingXXXL,
} from "src/styles/spacing";
import { Text, useSafeArea } from "src/swsh-native";
import { usePhotoReactionModalDimensions } from "./PhotoReactionModalSkeleton";
import LargeReactionAssetCluster from "./components/LargeReactionAssetCluster";
import renderReactionUserItem from "./helpers/renderReactionUserItem";
import usePhotoReactionUsers from "./hooks/usePhotoReactionUsers";

const PhotoReactionModal = () => {
	const style = usePhotoReactionModalDimensions();
	const {
		props: { gatheringId, photoId, numPrimaryReaction: numPrimaryReactionProp },
		closeSelf,
	} = useInsideModal<GatheringModal["PhotoReactionModal"]>();
	const { reactionAsset } = useGatheringReactionAsset(gatheringId);
	const {
		reactionUsers,
		loading,
		refreshing,
		fetchMore,
		refresh,
		numPrimaryReaction: numPrimaryReactionData,
	} = usePhotoReactionUsers({
		gatheringId,
		photoId,
	});

	const numPrimaryReaction = numPrimaryReactionData ?? numPrimaryReactionProp ?? 0;
	const { theme } = useStyle();
	const safeArea = useSafeArea();

	return (
		<View
			style={[
				style,
				{
					paddingTop: paddingLarge + safeArea.top,
					alignItems: "center",
				},
			]}
		>
			<View
				style={{
					flexDirection: "row",
					alignItems: "center",
					width: "100%",
					justifyContent: "center",
				}}
			>
				<Text semibold semiLarge testID="ReactionHeader">
					{abbreviateBigNumber(numPrimaryReaction)} Reaction{pluralS(numPrimaryReaction)}
				</Text>
				<Touchable
					onPress={closeSelf}
					containerStyle={{
						position: "absolute",
						left: paddingMedium,
						padding: paddingMedium,
					}}
				>
					<IconClose size={20} />
				</Touchable>
				{loading && !refreshing && (
					<Loading
						style={{
							position: "absolute",
							right: paddingXL,
						}}
					/>
				)}
			</View>
			<View
				style={{
					marginTop: paddingSmallSpacer,
					alignItems: "center",
					width: "86%",
					borderBottomColor: color.Divider[theme],
					borderBottomWidth: StyleSheet.hairlineWidth,
					borderStyle: "solid",
				}}
			>
				{!!reactionAsset && (
					<LargeReactionAssetCluster
						style={{
							marginBottom: paddingXXXL,
						}}
						numPrimaryReaction={numPrimaryReaction}
						reactionAsset={reactionAsset}
					/>
				)}
			</View>
			<View
				style={{
					flex: 1,
					width: "100%",
				}}
			>
				<FlashList
					data={reactionUsers}
					keyExtractor={(item, index) => item.user?.userId ?? index.toString()}
					renderScrollComponent={ScrollView}
					numColumns={3}
					renderItem={renderReactionUserItem}
					refreshing={refreshing}
					onRefresh={refresh}
					onEndReached={fetchMore}
					onEndReachedThreshold={0.5}
					contentContainerStyle={{
						paddingTop: paddingLarge,
						paddingBottom: safeArea.bottom + paddingLarge,
					}}
					estimatedItemSize={116}
				/>
			</View>
		</View>
	);
};
export default PhotoReactionModal;
